import {
  fsrs,
  createEmptyCard,
  Rating,
  State,
  type Card as FsrsCard,
  type Grade,
  type ReviewLog,
} from "ts-fsrs";
import type { Card, CardState, Direction, Enrollment, QueueItem, Rating as DbRating } from "./types";
import { startOfDay } from "./time";

/**
 * FSRS-6 scheduling.
 *
 * Chosen over SM-2 (which the original handoff used) because it needs ~20-30%
 * fewer reviews for the same retention, and because SM-2's single `ease` factor
 * stays permanently damaged after a few early misses — FSRS's difficulty
 * mean-reverts instead.
 *
 * Runs client-side. There is no adversary and nothing to gain by cheating your
 * own study schedule, so the server's job is atomicity (one transaction for
 * "append review + update state"), not verification. See submit_review().
 */

/**
 * Must stay in sync with the leech threshold in schema.sql's submit_review.
 * Past ~8 lapses a card isn't being learned, it's being ground — it gets
 * suspended so it stops eating sessions and can be reworded instead.
 */
export const LEECH_THRESHOLD = 8;

/** Two-button grading. FSRS is *slightly more accurate* with just these two. */
export type Answer = "again" | "good";

const ANSWER_TO_RATING: Record<Answer, Grade> = {
  again: Rating.Again, // 1
  good: Rating.Good, // 3
};

export function scheduler(requestRetention = 0.9) {
  return fsrs({
    request_retention: requestRetention,
    // Short-term steps mean a brand-new word is seen ~3 times in the first
    // session (1m, 10m, then graduated), which is where most of the initial
    // encoding happens. Worth the extra taps for vocabulary.
    enable_short_term: true,
    learning_steps: ["1m", "10m"],
    relearning_steps: ["10m"],
  });
}

// ---------------------------------------------------------------------------
// Conversion. Column names in card_states match the ts-fsrs Card interface
// one-for-one, so these are straight copies — no field mapping to get wrong.
// ---------------------------------------------------------------------------

/** A never-studied card converts to a fresh FSRS card (state New, due now). */
export function toFsrs(state: CardState | null, now: Date = new Date()): FsrsCard {
  if (!state) return createEmptyCard(now);
  return {
    due: new Date(state.due),
    stability: state.stability,
    difficulty: state.difficulty,
    elapsed_days: state.elapsed_days,
    scheduled_days: state.scheduled_days,
    learning_steps: state.learning_steps,
    reps: state.reps,
    lapses: state.lapses,
    state: state.state as State,
    last_review: state.last_review ? new Date(state.last_review) : undefined,
  };
}

/** Serialize for the submit_review / undo_review RPC `p_state` jsonb argument. */
export function toJson(card: FsrsCard): Record<string, unknown> {
  return {
    due: card.due.toISOString(),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    learning_steps: card.learning_steps,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state,
    last_review: card.last_review ? card.last_review.toISOString() : null,
  };
}

// ---------------------------------------------------------------------------
// Grading
// ---------------------------------------------------------------------------

export interface GradeResult {
  card: FsrsCard;
  log: ReviewLog;
  rating: DbRating;
}

/** Apply an answer and get the new state plus the log row to persist. */
export function grade(
  state: CardState | null,
  answer: Answer,
  now: Date = new Date(),
  requestRetention?: number,
): GradeResult {
  const g = ANSWER_TO_RATING[answer];
  const { card, log } = scheduler(requestRetention).next(toFsrs(state, now), now, g);
  return { card, log, rating: g as DbRating };
}

/**
 * Reverse the most recent grading — the client half of undo.
 * Mis-tapping "didn't know" on a mature card otherwise costs weeks of interval,
 * so this needs to exist from day one.
 */
export function rollback(card: FsrsCard, log: ReviewLog): FsrsCard {
  return scheduler().rollback(card, log);
}

/** What each button would do, for showing "Again · 1m / Good · 10d" on the card. */
export function previewIntervals(
  state: CardState | null,
  now: Date = new Date(),
  requestRetention?: number,
): Record<Answer, Date> {
  const s = scheduler(requestRetention);
  return {
    again: s.next(toFsrs(state, now), now, Rating.Again).card.due,
    good: s.next(toFsrs(state, now), now, Rating.Good).card.due,
  };
}

/** 0..1 probability of recalling right now. Useful for stats and debugging. */
export function retrievability(state: CardState | null, now: Date = new Date()): number {
  if (!state) return 0;
  return scheduler().get_retrievability(toFsrs(state, now), now, false);
}

// ---------------------------------------------------------------------------
// Queue building — pure function so it can be unit-tested without a database.
// ---------------------------------------------------------------------------

export interface QueueInput {
  cards: Card[];
  /** Existing states, keyed `${card_id}:${direction}`. */
  states: Map<string, CardState>;
  enrollment: Pick<Enrollment, "new_per_day" | "directions">;
  /** Count of cards first introduced today, in the user's timezone. */
  introducedToday: number;
  tz: string;
  now?: Date;
}

export function stateKey(cardId: string, direction: Direction): string {
  return `${cardId}:${direction}`;
}

/**
 * Decide what to show, in what order.
 *
 * Rules, in priority order:
 *  1. Suspended cards never appear.
 *  2. Due cards (already learned, interval elapsed) come first — forgetting
 *     something already invested in is worse than delaying something new.
 *  3. New cards are capped at `new_per_day` minus those already introduced
 *     today, counted in the learner's own timezone. Without this cap, adding a
 *     1,000-card season buries the user on day one and they quit.
 *  4. Siblings are buried: if both directions of one card are eligible, only the
 *     first appears this session. Seeing "book/کتاب" then "کتاب/book" back to
 *     back tests recognition twice, not recall.
 *  5. New cards are interleaved into the due queue rather than appended, so a
 *     long session doesn't end with an unbroken wall of unfamiliar words.
 */
export function buildQueue(input: QueueInput): QueueItem[] {
  const now = input.now ?? new Date();
  const dayStart = startOfDay(input.tz, now);
  void dayStart; // caller already used it to compute introducedToday

  const directions = input.enrollment.directions.length ? input.enrollment.directions : [0 as Direction];

  const due: QueueItem[] = [];
  const fresh: QueueItem[] = [];
  const seenCards = new Set<string>();

  for (const card of input.cards) {
    for (const direction of directions) {
      const state = input.states.get(stateKey(card.id, direction)) ?? null;

      if (state?.suspended) continue;
      if (seenCards.has(card.id)) continue; // rule 4: bury the sibling

      if (!state) {
        fresh.push({ card, direction, state: null });
        seenCards.add(card.id);
      } else if (new Date(state.due).getTime() <= now.getTime()) {
        due.push({ card, direction, state });
        seenCards.add(card.id);
      }
    }
  }

  // Oldest-due first: those are the most at risk of being forgotten.
  due.sort((a, b) => new Date(a.state!.due).getTime() - new Date(b.state!.due).getTime());

  const newBudget = Math.max(0, input.enrollment.new_per_day - input.introducedToday);
  const admitted = fresh.slice(0, newBudget);

  return interleave(due, admitted);
}

/**
 * Spread `extra` evenly through `base` rather than concatenating, so new words
 * arrive at a steady trickle instead of all at the end.
 */
function interleave<T>(base: T[], extra: T[]): T[] {
  if (!extra.length) return base;
  if (!base.length) return extra;

  const out: T[] = [];
  const gap = base.length / extra.length;
  let nextExtra = 0;

  for (let i = 0; i < base.length; i++) {
    out.push(base[i]!);
    while (nextExtra < extra.length && (nextExtra + 1) * gap <= i + 1) {
      out.push(extra[nextExtra]!);
      nextExtra++;
    }
  }
  while (nextExtra < extra.length) out.push(extra[nextExtra++]!);

  return out;
}

/** Human label for an SRS state, for debug output. */
export function stateName(state: number): string {
  return State[state as State] ?? `unknown(${state})`;
}
